/**
 * Recursion utilities for preventing infinite loops and stack overflow
 */

/**
 * Creates a safe wrapper that prevents infinite recursion
 * @param {Function} fn - Function to wrap
 * @param {number} maxDepth - Maximum recursion depth (default: 100)
 * @param {string} context - Context for logging
 * @returns {Function} - Safe wrapped function
 */
export const createSafeRecursiveFunction = (fn, maxDepth = 100, context = 'Unknown') => {
    let depth = 0;
    const maxDepthReached = new Error(`Maximum recursion depth (${maxDepth}) exceeded in ${context}`);
    
    return function safeRecursiveFunction(...args) {
        depth++;
        
        if (depth > maxDepth) {
            console.error(`Infinite recursion detected in ${context}:`, maxDepthReached);
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(maxDepthReached, {
                    tags: {
                        errorType: 'INFINITE_RECURSION',
                        context: context,
                        maxDepth: maxDepth,
                        currentDepth: depth
                    }
                });
            }
            
            throw maxDepthReached;
        }
        
        try {
            const result = fn.apply(this, args);
            depth--;
            return result;
        } catch (error) {
            depth--;
            throw error;
        }
    };
};

/**
 * Creates a debounced function that prevents rapid successive calls
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in milliseconds (default: 100)
 * @param {string} context - Context for logging
 * @returns {Function} - Debounced function
 */
export const createDebouncedFunction = (fn, delay = 100, context = 'Unknown') => {
    let timeoutId = null;
    let callCount = 0;
    const maxCallsPerSecond = 10; // Prevent excessive calls
    
    return function debouncedFunction(...args) {
        callCount++;
        
        // Prevent excessive calls
        if (callCount > maxCallsPerSecond) {
            console.warn(`Excessive calls detected in ${context}: ${callCount} calls per second`);
            return;
        }
        
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            callCount = 0; // Reset counter
            fn.apply(this, args);
        }, delay);
    };
};

/**
 * Creates a throttled function that limits execution frequency
 * @param {Function} fn - Function to throttle
 * @param {number} limit - Time limit in milliseconds (default: 100)
 * @param {string} context - Context for logging
 * @returns {Function} - Throttled function
 */
export const createThrottledFunction = (fn, limit = 100, context = 'Unknown') => {
    let inThrottle = false;
    let callCount = 0;
    const maxCallsPerSecond = 10;
    
    return function throttledFunction(...args) {
        callCount++;
        
        if (callCount > maxCallsPerSecond) {
            console.warn(`Excessive calls detected in ${context}: ${callCount} calls per second`);
            return;
        }
        
        if (!inThrottle) {
            fn.apply(this, args);
            inThrottle = true;
            setTimeout(() => {
                inThrottle = false;
                callCount = 0; // Reset counter
            }, limit);
        }
    };
};

/**
 * Monitors function calls for potential infinite loops
 * @param {Function} fn - Function to monitor
 * @param {string} context - Context for logging
 * @param {number} maxCalls - Maximum calls per second (default: 50)
 * @returns {Function} - Monitored function
 */
export const createMonitoredFunction = (fn, context = 'Unknown', maxCalls = 50) => {
    const callTimes = [];
    const maxCallsPerSecond = maxCalls;
    
    return function monitoredFunction(...args) {
        const now = Date.now();
        
        // Remove calls older than 1 second
        while (callTimes.length > 0 && now - callTimes[0] > 1000) {
            callTimes.shift();
        }
        
        // Check if we're exceeding the limit
        if (callTimes.length >= maxCallsPerSecond) {
            const error = new Error(`Function call limit exceeded in ${context}: ${callTimes.length} calls per second`);
            console.error('Infinite loop detected:', error);
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(error, {
                    tags: {
                        errorType: 'INFINITE_LOOP',
                        context: context,
                        callsPerSecond: callTimes.length,
                        maxCalls: maxCallsPerSecond
                    }
                });
            }
            
            throw error;
        }
        
        callTimes.push(now);
        return fn.apply(this, args);
    };
};

/**
 * React hook for safe useEffect with recursion protection
 * @param {Function} effect - Effect function
 * @param {Array} deps - Dependencies array
 * @param {string} context - Context for logging
 * @returns {void}
 */
export const useSafeEffect = (effect, deps, context = 'useEffect') => {
    const effectRef = React.useRef();
    const depthRef = React.useRef(0);
    const maxDepth = 10; // Maximum effect depth
    
    React.useEffect(() => {
        depthRef.current++;
        
        if (depthRef.current > maxDepth) {
            console.error(`Infinite useEffect detected in ${context}:`, {
                depth: depthRef.current,
                maxDepth: maxDepth,
                deps: deps
            });
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(new Error(`Infinite useEffect in ${context}`), {
                    tags: {
                        errorType: 'INFINITE_USEEFFECT',
                        context: context,
                        depth: depthRef.current,
                        maxDepth: maxDepth
                    }
                });
            }
            
            return;
        }
        
        const cleanup = effect();
        depthRef.current--;
        
        return cleanup;
    }, deps);
};

/**
 * Detects potential infinite recursion in a function
 * @param {Function} fn - Function to test
 * @param {Array} testArgs - Test arguments
 * @param {number} maxDepth - Maximum depth to test
 * @returns {Object} - Test result
 */
export const detectInfiniteRecursion = (fn, testArgs = [], maxDepth = 50) => {
    let depth = 0;
    let hasInfiniteRecursion = false;
    let error = null;
    
    const originalFn = fn;
    const wrappedFn = function(...args) {
        depth++;
        
        if (depth > maxDepth) {
            hasInfiniteRecursion = true;
            error = new Error(`Infinite recursion detected at depth ${depth}`);
            return;
        }
        
        try {
            const result = originalFn.apply(this, args);
            depth--;
            return result;
        } catch (err) {
            depth--;
            throw err;
        }
    };
    
    try {
        wrappedFn(...testArgs);
    } catch (err) {
        error = err;
    }
    
    return {
        hasInfiniteRecursion,
        depth,
        error,
        maxDepth
    };
};

/**
 * Sets up global recursion monitoring
 */
export const setupRecursionMonitoring = () => {
    // Monitor for RangeError: Maximum call stack size exceeded
    window.addEventListener('error', (event) => {
        if (event.error && event.error.name === 'RangeError' && 
            event.error.message.includes('Maximum call stack size exceeded')) {
            
            console.error('Stack overflow detected:', event.error);
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(event.error, {
                    tags: {
                        errorType: 'STACK_OVERFLOW',
                        context: 'GLOBAL_ERROR_HANDLER'
                    }
                });
            }
        }
    });
    
    // Monitor for unhandled promise rejections that might be recursion-related
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason && event.reason.name === 'RangeError' && 
            event.reason.message.includes('Maximum call stack size exceeded')) {
            
            console.error('Stack overflow in promise:', event.reason);
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(event.reason, {
                    tags: {
                        errorType: 'STACK_OVERFLOW_PROMISE',
                        context: 'UNHANDLED_REJECTION'
                    }
                });
            }
        }
    });
};

export default {
    createSafeRecursiveFunction,
    createDebouncedFunction,
    createThrottledFunction,
    createMonitoredFunction,
    useSafeEffect,
    detectInfiniteRecursion,
    setupRecursionMonitoring
};
